import { Component, Element, Host, h, Prop, State, Method } from '@stencil/core';
import { Direction } from '../../entities';

@Component({
  tag: 'me-sticky',
  styleUrl: 'me-sticky.scss',
  shadow: false,
})
export class MeSticky {

  @Element() host: HTMLMeStickyElement;

  @Prop({ mutable: true }) height: null | number = null;

  @State() private elementHeight?: number;

  private element?: HTMLDivElement;
  private elementSizeObserver?: ResizeObserver;

  /**
   * Returns whether sticky behavior is on or off
   */
  private get isOn(): boolean {
    return this.height !== null;
  }

  /**
   * Returns whether `this.host` is below the current scroll position
   */
  private get isBelowScrollPosition(): boolean {
    return scrollY <= this.offsetY;
  }

  /**
   * Returns distance between the top of the document and the top of `this.host`
   */
  private get offsetY(): number {
    return this.host.getBoundingClientRect().top + scrollY;
  }

  componentDidLoad() {
    this.observeSizeChanges();
  }

  connectedCallback() {
    this.observeSizeChanges();
  }

  disconnectedCallback() {
    this.elementSizeObserver?.disconnect();
  }

  render() {
    return (
      <Host class="me-sticky" style={{ height: this.elementHeight ? this.elementHeight + 'px' : 'auto' }}>
        <div class="me-sticky__container" style={{ height: this.height ? this.height + 'px' : 'auto' }}>
          <div class="me-sticky__element" ref={el => this.element = el}>
            <slot></slot>
          </div>
        </div>
      </Host>
    );
  }

  @Method()
  async disable() {
    await this.enable(false);
  }

  @Method()
  async enable(on = true) {
    this.height = on ? scrollY : null;
  }

  @Method()
  async shouldScrollOut(scrollDirection: Direction) {
    return scrollDirection === Direction.Down
      && this.height >= (scrollY + this.elementHeight);
  }

  @Method()
  async shouldBeOff(scrollDirection: Direction) {
    return this.isOn
      && (
        this.isBelowScrollPosition
        || (
          scrollDirection === Direction.Down
          && this.height > this.elementHeight
          && this.height + this.offsetY < scrollY
        )
      );
  }

  /**
   * Sets height such that `this.element` scrolls out of view when user continues to scroll down
   */
  @Method()
  async scrollOut() {
    this.height = scrollY + this.elementHeight - this.offsetY;
  }

  private observeSizeChanges() {
    if (!this.element) { return; }

    this.elementSizeObserver?.disconnect();
    this.elementSizeObserver = new ResizeObserver(this.updateElementHeight.bind(this));

    this.elementSizeObserver.observe(this.element);
  }

  private updateElementHeight() {
    if (!this.element) { return; }

    this.elementHeight = this.element.getBoundingClientRect().height;
  }

}
